import { useState, useEffect, useCallback } from 'react';
import { Save, Plus, Trash2, ChevronUp, ChevronDown, Star, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function DropdownEditor({ label, data, onChange }) {
  const options = data.options || [];
  const defaultOpt = data.default_option || '';

  const add = () => onChange({ ...data, options: [...options, ''] });
  const remove = (i) => {
    const removed = options[i];
    const newOpts = options.filter((_, idx) => idx !== i);
    onChange({
      ...data,
      options: newOpts,
      default_option: defaultOpt === removed ? '' : defaultOpt,
    });
  };
  const update = (i, val) => {
    const old = options[i];
    const newOpts = options.map((o, idx) => idx === i ? val : o);
    onChange({
      ...data,
      options: newOpts,
      default_option: defaultOpt === old ? val : defaultOpt,
    });
  };
  const move = (i, dir) => {
    const next = [...options];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ ...data, options: next });
  };
  const setDefault = (val) => {
    onChange({ ...data, default_option: defaultOpt === val ? '' : val });
  };

  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-['Outfit'] text-sm font-bold text-white uppercase tracking-wider">{label}</h4>
        <button onClick={add} className="flex items-center gap-1 text-xs text-[#00E5FF] hover:text-white transition-colors">
          <Plus className="w-3 h-3" /> Add Option
        </button>
      </div>
      <div className="space-y-2">
        {options.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="flex flex-col gap-0.5">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-600 hover:text-white disabled:opacity-20 transition-colors"><ChevronUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => move(i, 1)} disabled={i === options.length - 1} className="text-slate-600 hover:text-white disabled:opacity-20 transition-colors"><ChevronDown className="w-3.5 h-3.5" /></button>
            </div>
            <input
              value={opt}
              onChange={(e) => update(i, e.target.value)}
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50"
              placeholder="Option text..."
            />
            <button
              onClick={() => setDefault(opt)}
              title={defaultOpt === opt ? 'Remove as default' : 'Set as default'}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${defaultOpt === opt ? 'bg-[#00E5FF]/20 text-[#00E5FF]' : 'text-slate-600 hover:text-amber-400 hover:bg-amber-500/10'}`}
            >
              <Star className="w-3.5 h-3.5" fill={defaultOpt === opt ? 'currentColor' : 'none'} />
            </button>
            <button onClick={() => remove(i)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-600 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      {defaultOpt && (
        <p className="text-[10px] text-slate-500 mt-3">Default: <span className="text-[#00E5FF]">{defaultOpt}</span></p>
      )}
    </div>
  );
}

export default function ContactFormManager() {
  const [dropdowns, setDropdowns] = useState({
    service: { options: [], default_option: '' },
    budget: { options: [], default_option: '' },
    care_plan: { options: [], default_option: '' },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/contact/settings`);
      const data = await res.json();
      if (data.dropdowns) setDropdowns(data.dropdowns);
    } catch (err) {
      console.error('Failed to fetch contact settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API}/api/admin/contact/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ dropdowns }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateDropdown = (key, data) => {
    setDropdowns(prev => ({ ...prev, [key]: data }));
  };

  if (loading) {
    return (
      <div data-testid="contact-form-manager" className="space-y-6">
        <div className="h-8 w-48 bg-white/5 rounded animate-pulse" />
        <div className="h-48 bg-white/5 rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <div data-testid="contact-form-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-['Outfit'] text-xl font-bold text-white">Contact Form</h2>
          <p className="text-sm text-slate-500 mt-0.5">Edit dropdown options, set defaults, and reorder choices.</p>
        </div>
        <div className="flex items-center gap-3">
          <a href="/contact" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#00E5FF] transition-colors">
            <ExternalLink className="w-3 h-3" /> Preview
          </a>
          {saved && <span className="text-xs text-emerald-400">Saved</span>}
          <button
            data-testid="contact-settings-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save All'}
          </button>
        </div>
      </div>

      <div className="bg-[#111111] border border-white/5 rounded-2xl p-6 space-y-5">
        <DropdownEditor label="Service" data={dropdowns.service || {}} onChange={(d) => updateDropdown('service', d)} />
        <DropdownEditor label="Budget Range" data={dropdowns.budget || {}} onChange={(d) => updateDropdown('budget', d)} />
        <DropdownEditor label="Care Plan" data={dropdowns.care_plan || {}} onChange={(d) => updateDropdown('care_plan', d)} />
      </div>
    </div>
  );
}
